import React, { useState } from 'react';
import { Input, Select, Button, Space, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faBroom } from '@fortawesome/pro-solid-svg-icons';
import { PlannerPage } from '../../../services/tasks';
import { colors } from '../../../theme/colors';

const { Option } = Select;

interface FilterBarProps {
  plannerPage: PlannerPage;
  filters: {
    search?: string;
    assigneeId?: string;
    priority?: string;
    tags?: string[];
    status?: string;
  };
  onFiltersChange: (filters: any) => void;
  members?: Array<{ id: string; name: string; photoUrl?: string }>;
}

const FilterBar: React.FC<FilterBarProps> = ({ 
  plannerPage, 
  filters, 
  onFiltersChange,
  members = [],
}) => {
  const [searchText, setSearchText] = useState(filters.search || '');

  const handleSearch = (value: string) => {
    setSearchText(value);
    onFiltersChange({ ...filters, search: value });
  };

  const handleFilterChange = (key: string, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setSearchText('');
    onFiltersChange({});
  };

  const activeFilterCount = Object.values(filters).filter(v => 
    v !== undefined && v !== null && v !== '' && 
    (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  return (
    <div style={{ 
      background: colors.bgBase,
      padding: '10px 0 12px',
      marginBottom: 8,
      position: 'sticky',
      top: 0,
      zIndex: 25,
    }}>
      <Space wrap style={{ width: '100%' }} size={[10, 8]}>
        {/* Search */}
        <Input
          placeholder="Search tasks..."
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#BFBFBF' }} />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ 
            width: 270,
            background: '#2a2a2a',
            border: '1px solid #444',
            color: '#F2F2F2',
          }}
          allowClear
        />

        {/* Status Filter */}
        <Select
          placeholder="Status"
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
          allowClear
          style={{ width: 140 }}
          dropdownStyle={{ background: '#1a1a1a' }}
        >
          {plannerPage.statuses.map(status => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>

        {/* Priority Filter */}
        <Select
          placeholder="Priority"
          value={filters.priority}
          onChange={(value) => handleFilterChange('priority', value)}
          allowClear
          style={{ width: 140 }}
        >
          <Option value="LOW">Low</Option>
          <Option value="MEDIUM">Medium</Option>
          <Option value="HIGH">High</Option>
          <Option value="URGENT">Urgent</Option>
        </Select>

        {/* Assignee Filter */}
        {members.length > 0 && (
          <Select
            placeholder="Assignee"
            value={filters.assigneeId}
            onChange={(value) => handleFilterChange('assigneeId', value)}
            allowClear
            style={{ width: 170 }}
            showSearch
            optionFilterProp="children"
          >
            {members.map(member => (
              <Option key={member.id} value={member.id}>
                {member.name}
              </Option>
            ))}
          </Select>
        )}

        {/* Clear Filters */}
        {activeFilterCount > 0 && (
          <Button
            icon={<FontAwesomeIcon icon={faBroom} />}
            onClick={clearFilters}
            style={{ color: '#BFBFBF' }}
          >
            Clear ({activeFilterCount})
          </Button>
        )}

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {filters.status && (
              <Tag 
                closable 
                onClose={() => handleFilterChange('status', undefined)}
                color="#DB50AC"
              >
                Status: {filters.status}
              </Tag>
            )}
            {filters.priority && (
              <Tag 
                closable 
                onClose={() => handleFilterChange('priority', undefined)}
                color="#DB50AC"
              >
                Priority: {filters.priority}
              </Tag>
            )}
            {filters.assigneeId && (
              <Tag 
                closable 
                onClose={() => handleFilterChange('assigneeId', undefined)}
                color="#DB50AC"
              >
                Assignee: {members.find(m => m.id === filters.assigneeId)?.name}
              </Tag>
            )}
            {filters.search && (
              <Tag 
                closable 
                onClose={() => handleSearch('')}
                color="#DB50AC"
              >
                Search: {filters.search}
              </Tag>
            )}
          </div>
        )}
      </Space>
    </div>
  );
};

export default FilterBar;
